/* eslint-disable max-lines */
import { FC, useState } from 'react';

import FilterList from '@mui/icons-material/FilterList';
import {
  Box,
  BoxProps,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import * as Colors from '../theme/colors';

interface SelectFilterProps extends Omit<BoxProps, 'onChange'> {
  label: string;
  options: Array<{ label: string; value: string }>;
  currentValues: Array<string>;
  onChange: (values: Array<string>) => void;
  selectionColor?: string;
  activeBackgroundColor?: string;
  backgroundColor?: string;
}

export const SelectFilter: FC<SelectFilterProps> = ({
  label,
  options,
  currentValues,
  onChange,
  selectionColor = Colors.blue['500'],
  activeBackgroundColor = Colors.blueOpacities['12p'],
  backgroundColor = Colors.grey['50'],
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: React.FormEvent<HTMLDivElement>) => {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      onChange([...currentValues, checkbox.value]);
    } else {
      const filteredValues = currentValues.filter(
        (val) => checkbox.value !== val,
      );
      onChange(filteredValues);
    }
  };

  const handleClear = () => {
    if (currentValues.length > 0) {
      onChange([]);
    }
  };

  return (
    <Box {...props}>
      <Button
        id={`${label}-filter-menu`}
        aria-controls={open ? `${label}-filter-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="outlined"
        sx={{
          height: (theme) => theme.spacing(5.5),
          borderRadius: 8,
          borderColor: open ? selectionColor : 'action.disabledBackground',
          color: 'text.primary',
          backgroundColor: open ? activeBackgroundColor : backgroundColor,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <FilterList fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="body2" sx={{ mr: 1 }}>
          {label}
        </Typography>
        {currentValues.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: (theme) => theme.spacing(2.5),
              fontSize: '1rem',
              color: (theme) => theme.palette.getContrastText(selectionColor),
              backgroundColor: selectionColor,
              borderRadius: '999px',
              px: 0.8,
            }}
          >
            {currentValues.length}
          </Box>
        ) : null}
      </Button>
      <FormControl onChange={handleChange}>
        <Menu
          id={`${label}-filter-menu`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 48,
            horizontal: 'left',
          }}
          MenuListProps={{
            'aria-labelledby': `${label}-filter-button`,
          }}
        >
          <MenuItem
            disableRipple
            sx={{
              height: '100%',
              py: 0.5,
              px: 1,
              minWidth: (theme) => theme.spacing(27),
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Stack
              direction="column"
              sx={{
                borderBottom: (theme) => ({
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }),
                width: '100%',
              }}
            >
              <Stack direction="row" alignItems="center" sx={{ pr: 1 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ px: 1, py: 1.5 }}
                >
                  Filter by:
                </Typography>
              </Stack>
              <Stack
                direction="column"
                sx={{ maxHeight: '260px', overflow: 'auto' }}
              >
                {options.map((opt) => {
                  return (
                    <FormControlLabel
                      key={opt.value}
                      sx={{ pr: 1, mx: 0 }}
                      control={
                        <Checkbox
                          key={opt.value}
                          sx={{ p: 1 }}
                          value={opt.value}
                          checked={currentValues.includes(opt.value)}
                        />
                      }
                      label={opt.label}
                    />
                  );
                })}
              </Stack>
            </Stack>
            <Box onClick={handleClear}>
              <Typography
                variant="body2"
                sx={{
                  width: 'fit-content',
                  color:
                    currentValues.length > 0 ? selectionColor : 'action.active',
                  p: 1.5,
                }}
              >
                Clear Filters
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </FormControl>
    </Box>
  );
};
